import React, { useState, useEffect, useRef } from "react";
import { Chat } from "../../redux/reducers/ChatReducer/ChatSliceTypes";
import { User } from "../../redux/reducers/UserSlice/UserSliceType";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import { resetCount } from "../../redux/reducers/ChatReducer/ChatSlice";
import "react-loading-skeleton/dist/skeleton.css";
import {
  defaultProfileImage,
  defaultPropertyImage,
} from "../../utils/defaultImages";
import { getUnreadChatCount } from "../../redux/reducers/UserSlice/UserSlice";
import "./Chat.scss";
import moment from "moment";

interface ChatListProps {
  loading: boolean;
  currentUser: User | null;
  chats: any;
  selectChat: any;
  setSelectedChat: React.Dispatch<React.SetStateAction<Chat | null>>;
  getAllMessages: (chatId: number, page: number) => void;
  loadMoreChats: () => void;
  setChats: React.Dispatch<React.SetStateAction<any[]>>;
  isLoadingMore: boolean;
}

export const getMessageTime = (time: string) => {
  if (!time) return null;

  const messageDate = moment.utc(time).local();
  const today = moment().startOf("day");
  const yesterday = moment().subtract(1, "day").startOf("day");

  if (messageDate.isSame(today, "day")) {
    return messageDate.format("hh:mm A");
  } else if (messageDate.isSame(yesterday, "day")) {
    return "Yesterday";
  } else if (messageDate.isAfter(moment().subtract(7, "days"))) {
    return messageDate.format("ddd"); // Show day name for last week
  } else {
    return messageDate.format("MMM DD");
  }
};

const ChatList: React.FC<ChatListProps> = ({
  chats,
  loading,
  selectChat,
  setSelectedChat,
  getAllMessages,
  currentUser,
  loadMoreChats,
  setChats,
  isLoadingMore,
}) => {
  const chatListRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const unreadChatCount = useSelector(
    (state: RootState) => state.user.unreadChatCount
  );

  const handleScroll = () => {
    if (chatListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatListRef.current;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      
      if (scrollPercentage > 0.95 && !loading && !isScrolledToBottom && !isLoadingMore) {
        setIsScrolledToBottom(true);
        loadMoreChats();
      } else if (scrollPercentage < 0.95) {
        setIsScrolledToBottom(false);
      }
    }
  };

  useEffect(() => {
    const chatListElement = chatListRef.current;
    if (chatListElement) {
      chatListElement.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (chatListElement) {
        chatListElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [loading, isScrolledToBottom, isLoadingMore]);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!loading) {
      setIsScrolledToBottom(false);
    }
  }, [loading]);

  // Map chats to show the other person's info
  const finalChats = chats?.map((chat: any) =>
    chat.sender?.id === currentUser?.id
      ? { ...chat, show: chat.receiver }
      : { ...chat, show: chat.sender }
  );

  const getName = (name: string) => {
    if (!name) return "Unknown";
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const getLastMessage = (lastMessage: string) => {
    if (!lastMessage) return "No messages yet";
    return lastMessage.length > 50 
      ? lastMessage.substring(0, 50) + "..." 
      : lastMessage;
  };

  useEffect(() => {
    if (unreadChatCount > 0) {
      dispatch(getUnreadChatCount());
    }
  }, [dispatch, unreadChatCount]);

 const chatHandler = async (chat: any) => {
    console.log("🔵 chatHandler called for chat:", chat.id);
    console.log("🔵 Original unread_count:", chat.unread_count);
    
    if (selectChat?.id === chat.id) return;

    const originalUnreadCount = chat.unread_count;
    setSelectedChat(chat);

    // Optimistically update UI
    dispatch(resetCount(chat.id));
    console.log("🟢 Dispatched resetCount");
    
    setChats((prevChats) => {
      const updated = prevChats.map((c) => (c.id === chat.id ? { ...c, unread_count: 0 } : c));
      console.log("🟢 Updated chats state:", updated.find(c => c.id === chat.id));
      return updated;
    });

    try {
      console.log("🔵 Fetching messages...");
      await getAllMessages(chat.id, 1);
      console.log("🟢 Messages fetched");
      
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log("🔵 Fetching unread count...");
      
      const result = await dispatch(getUnreadChatCount());
      console.log("🟢 Unread count updated:", result);
      
    } catch (error) {
    console.error("🔴 Error:", error);
      // ... rollback code
    }
 };

  return (
    <div
      ref={chatListRef}
      className="h-full overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-neutral-300 dark:scrollbar-thumb-neutral-600"
    >
      <div className="space-y-2 p-2">
        {isLoading ? (
          // Skeleton Loaders
          Array(chats?.length || 5)
            .fill(null)
            .map((_, index) => (
              <div
                key={index}
                className="flex items-center p-3 bg-white dark:bg-neutral-800 rounded-xl animate-pulse border border-neutral-200 dark:border-neutral-700"
              >
                {/* Avatar Skeleton */}
                <div className="relative flex-shrink-0 mr-3">
                  <div className="w-14 h-14 bg-neutral-300 dark:bg-neutral-600 rounded-xl"></div>
                  <div className="w-8 h-8 bg-neutral-400 dark:bg-neutral-500 rounded-full absolute -bottom-1 -right-1 border-2 border-white dark:border-neutral-800"></div>
                </div>
                {/* Content Skeleton */}
                <div className="flex-1 space-y-2 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="h-4 bg-neutral-300 dark:bg-neutral-600 rounded w-32"></div>
                    <div className="h-3 bg-neutral-300 dark:bg-neutral-600 rounded w-16"></div>
                  </div>
                  <div className="h-3 bg-neutral-300 dark:bg-neutral-600 rounded w-3/4"></div>
                </div>
              </div>
            ))
        ) : finalChats && finalChats.length > 0 ? (
          finalChats.map((chat: any, index: number) => {
            const isSelected = selectChat?.id === chat?.id;
            const hasUnread = chat.unread_count > 0;

            return (
              <button
                key={chat.id || index}
                className={`w-full flex items-center p-3 rounded-xl transition-all duration-200 border-2 ${
                  isSelected
                    ? "bg-[#F56948] bg-opacity-10 border-[#F56948] cursor-default"
                    : "bg-white dark:bg-neutral-800 border-transparent hover:border-neutral-300 dark:hover:border-neutral-600 hover:shadow-md"
                } focus:outline-none focus:ring-2 focus:ring-[#F56948] focus:ring-opacity-50`}
                onClick={() => chatHandler(chat)}
                disabled={isSelected}
              >
                {/* Avatar Group */}
                <div className="relative flex-shrink-0 mr-3">
                  {/* Property Image */}
                  <div className="w-14 h-14 rounded-xl overflow-hidden ring-2 ring-white dark:ring-neutral-800 shadow-sm">
                    <img
                      src={chat.property?.image || defaultPropertyImage}
                      alt={chat.property?.title || "Property"}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = defaultPropertyImage;
                      }}
                    />
                  </div>
                  
                  {/* User Avatars Overlay */}
                  <div className="absolute -bottom-1 -right-1 flex items-center gap-0.5">
                    {chat.sender?.image ? (
                      <img
                        src={chat.sender.image}
                        alt={chat.sender?.name || "Sender"}
                        className="w-7 h-7 rounded-full border-2 border-white dark:border-neutral-800 shadow-md"
                        onError={(e) => {
                          e.currentTarget.src = defaultProfileImage;
                        }}
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full border-2 border-white dark:border-neutral-800 bg-gradient-to-br from-[#F56948] to-[#E5593D] flex items-center justify-center text-white text-xs font-bold shadow-md">
                        {chat.sender?.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                    )}
                    
                    {chat.receiver?.image ? (
                      <img
                        src={chat.receiver.image}
                        alt={chat.receiver?.name || "Receiver"}
                        className="w-7 h-7 rounded-full border-2 border-white dark:border-neutral-800 shadow-md -ml-2"
                        onError={(e) => {
                          e.currentTarget.src = defaultProfileImage;
                        }}
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full border-2 border-white dark:border-neutral-800 bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold shadow-md -ml-2">
                        {chat.receiver?.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                    )}
                  </div>

                  {/* Online Indicator (if needed) */}
                  {hasUnread && (
                    <div className="absolute -top-1 -left-1 w-3 h-3 bg-[#F56948] rounded-full ring-2 ring-white dark:ring-neutral-800"></div>
                  )}
                </div>

                {/* Chat Content */}
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className={`text-sm font-semibold truncate ${
                      isSelected
                        ? "text-[#F56948]"
                        : "text-neutral-900 dark:text-white"
                    }`}>
                      {getName(chat.show?.name)}
                    </h3>
                    <span className="text-xs text-neutral-500 dark:text-neutral-400 ml-2 flex-shrink-0">
                      {getMessageTime(chat.last_message_time)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <p className={`text-xs truncate flex-1 pr-2 ${
                      hasUnread
                        ? "text-neutral-900 dark:text-white font-medium"
                        : "text-neutral-600 dark:text-neutral-400"
                    }`}>
                      {getLastMessage(chat.last_message)}
                    </p>
                    
                    {hasUnread && (
                      <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-[#F56948] rounded-full">
                        {chat.unread_count > 99 ? "99+" : chat.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        ) : (
          // Empty State
          <div className="flex flex-col items-center justify-center py-12 text-neutral-500 dark:text-neutral-400">
            <div className="w-16 h-16 mb-4 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <p className="text-base font-medium">No conversations yet</p>
            <p className="text-sm text-center max-w-xs mt-1">
              Start a conversation with a property owner
            </p>
          </div>
        )}
        
        {/* Loading More Indicator */}
        {isLoadingMore && (
          <div className="flex justify-center items-center py-4">
            <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
              <div className="w-5 h-5 border-2 border-neutral-300 border-t-[#F56948] rounded-full animate-spin"></div>
              <span>Loading more...</span>
            </div>
          </div>
        )}

        {/* Bottom Padding */}
        <div className="h-4"></div>
      </div>
    </div>
  );
};

export default ChatList;